import React, { useEffect, useRef, useState } from 'react';
import './FeaturedHealers.css';
import { Button } from '../Button/button';
import axios from 'axios';

const VISIBLE_CARDS = 3;
const SLIDE_INTERVAL = 4000;
const CARD_WIDTH = 320;
const CARD_GAP = 24;

const FeaturedHealers = () => {
  const [healers, setHealers] = useState([]);
  const [startIdx, setStartIdx] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef();

  useEffect(() => {
    const fetchHealers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/healers`);
        const healersData = response.data;

        // Fetch reviews for each healer to calculate ratings
        const healersWithReviews = await Promise.all(
          healersData.map(async (healer) => {
            try {
              const reviewsResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/review/${healer.id}`);
              const reviews = reviewsResponse.data;
              const avgRating = reviews.length > 0
                ? reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length
                : 0;

              return {
                id: healer.id,
                name: `${healer.firstname} ${healer.lastname}`,
                description: healer.description || 'Professional healer with years of experience',
                services: healer.services ? healer.services.split(',').slice(0, 3) : [],
                rating: Math.round(avgRating * 10) / 10, // Round to 1 decimal place
                reviews: reviews.length,
                email: healer.email
              };
            } catch (error) {
              console.error(`Error fetching reviews for healer ${healer.id}:`, error);
              return {
                id: healer.id,
                name: `${healer.firstname} ${healer.lastname}`,
                description: healer.description || 'Professional healer with years of experience',
                services: healer.services ? healer.services.split(',').slice(0, 3) : [],
                rating: 0,
                reviews: 0,
                email: healer.email
              };
            }
          })
        );

        setHealers(healersWithReviews);
        console.log('Fetched Healers with Reviews:', healersWithReviews);
      } catch (error) {
        console.error('Error fetching healers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHealers();
  }, []);

  const resetInterval = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setStartIdx((prev) => (prev + 1) % healers.length);
    }, SLIDE_INTERVAL);
  };

  useEffect(() => {
    if (healers.length > 0) resetInterval();
    return () => clearInterval(intervalRef.current);
  }, [healers]);

  const totalCards = healers.length;
  const extendedHealers = [...healers, ...healers.slice(0, VISIBLE_CARDS)];
  const translateX = -(startIdx * (CARD_WIDTH + CARD_GAP));

  useEffect(() => {
    if (startIdx === totalCards) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setStartIdx(0);
      }, 300);
      return () => clearTimeout(timeout);
    } else {
      setIsTransitioning(true);
    }
  }, [startIdx, totalCards]);

  const handlePrev = () => {
    setStartIdx((prev) => (prev - 1 + totalCards) % totalCards);
    setIsTransitioning(true);
    resetInterval();
  };

  const handleNext = () => {
    setStartIdx((prev) => (prev + 1) % totalCards);
    setIsTransitioning(true);
    resetInterval();
  };

  const handleBookNow = (healer) => {
    // Navigate to search page with healer pre-selected
    //window.location.href = `/search?healer=${healer.id}`;
    window.location.href = `/book/${healer.id}`
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<span key={i} style={{ color: '#FFD700', fontSize: 16 }}>★</span>);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<span key={i} style={{ color: '#FFD700', fontSize: 16 }}>☆</span>);
      } else {
        stars.push(<span key={i} style={{ color: '#E0E0E0', fontSize: 16 }}>☆</span>);
      }
    }
    return stars;
  };

  if (loading) {
    return (
      <section style={sectionStyle}>
        <h2 style={titleStyle}>Featured Healers</h2>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <div style={loaderStyle}></div>
        </div>
      </section>
    );
  }

  return (
    <section id = "featuredHealers"style={sectionStyle}>
      <h2 style={titleStyle}>Featured Healers</h2>
      <div id = "featuredHealersDiv"style={containerStyle}>
        {/* Left Arrow */}
        <button onClick={handlePrev} aria-label="Previous" style={arrowStyle('left')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9c70f3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Right Arrow */}
        <button onClick={handleNext} aria-label="Next" style={arrowStyle('right')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9c70f3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>

        {/* Card Container */}
        <div
            id = "listOfFeaturedHealers"
          style={{
            display: 'flex',
            gap: CARD_GAP,
            transform: `translateX(${translateX}px)`,
            transition: isTransitioning ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            willChange: 'transform',
          }}
        >
          {extendedHealers.map((healer, idx) => (
            <div key={`${healer.id}-${idx}`} style={cardStyle}>
              <div style={avatarStyle}>
                <span role="img" aria-label="avatar" style={{ fontSize: 32 }}>👤</span>
              </div>

              <div style={nameStyle}>{healer.name}</div>

              <div style={descriptionStyle}>{healer.description}</div>

              {/* Services */}
              {healer.services.length > 0 && (
                <div style={servicesStyle}>
                  <div style={servicesLabelStyle}>Services:</div>
                  <div style={servicesListStyle}>
                    {healer.services.map((service, index) => (
                      <span key={index} style={serviceTagStyle}>
                        {service.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Rating */}
              <div style={ratingContainerStyle}>
                <div style={starsStyle}>
                  {renderStars(healer.rating)}
                </div>
                <div style={ratingTextStyle}>
                  {healer.rating > 0 ? `${healer.rating} (${healer.reviews} reviews)` : 'No reviews yet'}
                </div>
              </div>

              <button
                onClick={() => handleBookNow(healer)}
                style={bookButtonStyle}
              >
                Book Now
              </button>
            </div>
          ))}
        </div>
      </div>

      <div id = "searchHealerDiv" style={seeMoreContainerStyle}>
        <a id = "searchHealer"href="/search" style={seeMoreLinkStyle}>
          <Button buttonStyle="btn--primary">See More Healers</Button>
        </a>
      </div>
    </section>
  );
};

// Styles
const sectionStyle = {
  background: '#fff',
  borderRadius: 24,
  boxShadow: '0 8px 32px rgba(156, 112, 243, 0.08)',
  padding: '3rem 2rem',
  width: '100%',
  margin: '0 auto 3rem auto',
  maxWidth: 1200,
};

const titleStyle = {
  textAlign: 'center',
  fontWeight: 700,
  color: '#1ABC9C',
  fontSize: '2.25rem',
  marginBottom: '2.5rem',
  letterSpacing: '-0.02em',
};

const containerStyle = {
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
  minHeight: 420,
  display: 'flex',
  alignItems: 'center',
};

const arrowStyle = (position) => ({
  position: 'absolute',
  [position]: -12,
  top: '50%',
  transform: 'translateY(-50%)',
  background: '#fff',
  border: '1px solid #f0f0f0',
  borderRadius: '50%',
  boxShadow: '0 4px 16px rgba(156, 112, 243, 0.12)',
  width: 44,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9c70f3',
  cursor: 'pointer',
  zIndex: 2,
  padding: 0,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-50%) scale(1.05)',
    boxShadow: '0 6px 20px rgba(156, 112, 243, 0.2)',
  },
});

const cardStyle = {
  background: 'linear-gradient(135deg, #9c70f3 0%, #8b5cf6 100%)',
  borderRadius: 20,
  minWidth: CARD_WIDTH,
  maxWidth: CARD_WIDTH,
  flex: `0 0 ${CARD_WIDTH}px`,
  color: '#fff',
  boxShadow: '0 8px 24px rgba(156, 112, 243, 0.15)',
  padding: '2rem 1.5rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(156, 112, 243, 0.25)',
  },
};

const avatarStyle = {
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.2)',
  marginBottom: '1.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
};

const nameStyle = {
  fontWeight: 700,
  fontSize: '1.25rem',
  marginBottom: '0.5rem',
  textAlign: 'center',
  lineHeight: 1.3,
};

const descriptionStyle = {
  fontWeight: 400,
  fontSize: '0.9rem',
  color: 'rgba(255, 255, 255, 0.9)',
  marginBottom: '1rem',
  textAlign: 'center',
  lineHeight: 1.4,
  minHeight: '2.8rem',
};

const servicesStyle = {
  width: '100%',
  marginBottom: '1rem',
};

const servicesLabelStyle = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.8)',
  marginBottom: '0.5rem',
  textAlign: 'center',
};

const servicesListStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.25rem',
  justifyContent: 'center',
};

const serviceTagStyle = {
  background: 'rgba(255, 255, 255, 0.2)',
  color: '#fff',
  padding: '0.25rem 0.5rem',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 500,
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

const ratingContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '1.5rem',
  gap: '0.25rem',
};

const starsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
};

const ratingTextStyle = {
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.8)',
  fontWeight: 500,
};

const bookButtonStyle = {
  background: '#fff',
  color: '#9c70f3',
  border: 'none',
  borderRadius: 12,
  padding: '0.75rem 1.5rem',
  fontWeight: 600,
  fontSize: '0.95rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  width: '100%',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
  },
};

const seeMoreContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: '2rem',
};

const seeMoreLinkStyle = {
  textDecoration: 'none',
};

const loaderStyle = {
  width: 40,
  height: 40,
  border: '3px solid #f3f3f3',
  borderTop: '3px solid #9c70f3',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

export default FeaturedHealers;
